import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import instantsearch from "instantsearch.js";
import { PORT_NUMBER, COURSE_FIELDS, LESSON_FIELDS } from "./constants";
import { TypesenseServiceOptions } from "./types";

export class TypesenseService {
  private adapter: TypesenseInstantSearchAdapter;
  private searchInstance: ReturnType<typeof instantsearch>;

  constructor(options: TypesenseServiceOptions) {
    this.adapter = this.createAdapter(options);
    this.searchInstance = this.createSearchInstance(options);
  }

  private createAdapter(
    options: TypesenseServiceOptions
  ): TypesenseInstantSearchAdapter {
    const { config, includeLessonIndex = false } = options;

    const collectionSpecificSearchParameters: Record<
      string,
      {
        preset: string;
        include_fields: string;
      }
    > = {
      [config.courseIndexName]: {
        preset: config.coursePresetName,
        include_fields: COURSE_FIELDS,
      },
    };

    // Only add lesson index configuration if needed and available
    if (
      includeLessonIndex &&
      config.lessonIndexName &&
      config.lessonPresetName
    ) {
      collectionSpecificSearchParameters[config.lessonIndexName] = {
        preset: config.lessonPresetName,
        include_fields: LESSON_FIELDS,
      };
    }

    return new TypesenseInstantSearchAdapter({
      server: {
        apiKey: config.apiKey,
        cacheSearchResultsForSeconds: 0,
        nearestNode: {
          host: config.nearestNodeHost,
          port: PORT_NUMBER,
          protocol: "https",
        },
        nodes: this.createNodes(config.nodesHosts),
      },
      collectionSpecificSearchParameters,
    });
  }

  private createSearchInstance(
    options: TypesenseServiceOptions
  ): ReturnType<typeof instantsearch> {
    const { searchClient } = this.adapter;
    const { config, initialUiState, onStateChange } = options;

    const searchInstanceOptions: Parameters<typeof instantsearch>[0] = {
      searchClient,
      indexName: config.courseIndexName,
    };

    if (initialUiState) {
      searchInstanceOptions.initialUiState = initialUiState;
    }

    if (onStateChange) {
      searchInstanceOptions.onStateChange = onStateChange;
    }

    return instantsearch(searchInstanceOptions);
  }

  private createNodes(nodesHosts: string[]) {
    return nodesHosts.map((node) => ({
      host: node,
      port: PORT_NUMBER,
      protocol: "https",
    }));
  }

  public getSearchClient() {
    return this.adapter.searchClient;
  }

  public getSearchInstance() {
    return this.searchInstance;
  }

  public getAdapter() {
    return this.adapter;
  }

  public isAvailable(): boolean {
    return !!this.adapter && !!this.searchInstance;
  }
}
